import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID, HostListener } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxChartsModule, Color, ScaleType, LegendPosition } from '@swimlane/ngx-charts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MarketPredictionsService } from '../../services/market-predictions.service';
import { MarketPredictionsResponse, TranslatedText } from '../../models/market-predictions.models';
import { formatDateLocalized, formatMonthLocalized } from '../../utils/date-utils';

/**
 * Market Predictions Page Component
 * Displays AI-powered market predictions and insights
 */
@Component({
  selector: 'app-market-predictions-page',
  standalone: true,
  imports: [CommonModule, TranslocoModule, MatCardModule, MatExpansionModule, MatChipsModule, MatProgressSpinnerModule, NgxChartsModule],
  templateUrl: './market-predictions-page.component.html',
  styleUrls: ['./market-predictions-page.component.scss'],
})
export class MarketPredictionsPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  readonly isBrowser: boolean;

  predictions: MarketPredictionsResponse | null = null;
  loading = true;
  error = false;

  // Comparison Chart Data
  comparisonChartData: any[] = [];

  // Chart configuration
  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showLegend = true;
  showXAxisLabel = true;
  showYAxisLabel = true;
  animations = true;
  schemeType = ScaleType.Ordinal;

  // Responsive legend position
  legendPosition: LegendPosition = LegendPosition.Right;
  private readonly MOBILE_BREAKPOINT = 768;

  colorScheme: Color = {
    name: 'comparison',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#3b82f6', '#10b981', '#f59e0b'], // Blue (predicted), Green (actual), Orange (current)
  };

  constructor(
    private readonly marketPredictionsService: MarketPredictionsService,
    private readonly translocoService: TranslocoService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    // Set initial legend position based on screen size
    this.updateLegendPosition();

    // Only load predictions in browser, not during SSR
    if (this.isBrowser) {
      this.loadPredictions();

      // Subscribe to language changes and re-transform chart data
      this.translocoService.langChanges$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.predictions) {
          this.buildComparisonChartData();
        }
      });
    } else {
      // During SSR, set loading to false to show a placeholder
      this.loading = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event?: Event) {
    this.updateLegendPosition();
  }

  private updateLegendPosition(): void {
    const width = window.innerWidth;
    this.legendPosition = width <= this.MOBILE_BREAKPOINT ? LegendPosition.Below : LegendPosition.Right;
  }

  /**
   * Load latest market predictions
   */
  private loadPredictions(): void {
    this.loading = true;
    this.error = false;

    this.marketPredictionsService.getLatestPredictions().subscribe({
      next: (data) => {
        this.loading = false;
        if (data) {
          this.predictions = data;
          this.error = false;
          this.buildComparisonChartData();
        } else {
          this.predictions = null;
          this.error = true;
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = true;
        this.predictions = null;
      },
    });
  }

  /**
   * Build comparison chart data showing predicted vs actual vs current
   */
  private buildComparisonChartData(): void {
    if (!this.predictions?.prevState) {
      return;
    }

    const prevPredicted = this.predictions.prevState.globalPrediction;
    const prevActual = this.predictions.prevState.globalActualData;
    const currentPredicted = this.predictions.predictedState.global;

    // Build chart data for each metric
    this.comparisonChartData = [
      {
        name: this.translocoService.translate('predictions.metrics.jobs'),
        series: [
          { name: this.translocoService.translate('predictions.chart.prevPredicted'), value: prevPredicted?.jobs || 0 },
          { name: this.translocoService.translate('predictions.chart.prevActual'), value: prevActual?.jobs || 0 },
          { name: this.translocoService.translate('predictions.chart.currentPredicted'), value: currentPredicted.jobs },
        ],
      },
      {
        name: this.translocoService.translate('predictions.metrics.candidates'),
        series: [
          { name: this.translocoService.translate('predictions.chart.prevPredicted'), value: prevPredicted?.candidates || 0 },
          { name: this.translocoService.translate('predictions.chart.prevActual'), value: prevActual?.candidates || 0 },
          { name: this.translocoService.translate('predictions.chart.currentPredicted'), value: currentPredicted.candidates },
        ],
      },
      {
        name: this.translocoService.translate('predictions.metrics.applications'),
        series: [
          { name: this.translocoService.translate('predictions.chart.prevPredicted'), value: prevPredicted?.applications || 0 },
          { name: this.translocoService.translate('predictions.chart.prevActual'), value: prevActual?.applications || 0 },
          { name: this.translocoService.translate('predictions.chart.currentPredicted'), value: currentPredicted.applications },
        ],
      },
      {
        name: this.translocoService.translate('predictions.metrics.jobsCreated'),
        series: [
          { name: this.translocoService.translate('predictions.chart.prevPredicted'), value: prevPredicted?.jobs_created || 0 },
          { name: this.translocoService.translate('predictions.chart.prevActual'), value: prevActual?.jobs_created || 0 },
          { name: this.translocoService.translate('predictions.chart.currentPredicted'), value: currentPredicted.jobs_created },
        ],
      },
      {
        name: this.translocoService.translate('predictions.metrics.candidatesRegistered'),
        series: [
          { name: this.translocoService.translate('predictions.chart.prevPredicted'), value: prevPredicted?.candidates_reg || 0 },
          { name: this.translocoService.translate('predictions.chart.prevActual'), value: prevActual?.candidates_reg || 0 },
          { name: this.translocoService.translate('predictions.chart.currentPredicted'), value: currentPredicted.candidates_reg },
        ],
      },
      {
        name: this.translocoService.translate('predictions.metrics.hiringRate') + ' (%)',
        series: [
          { name: this.translocoService.translate('predictions.chart.prevPredicted'), value: prevPredicted?.hiring_rate || 0 },
          { name: this.translocoService.translate('predictions.chart.prevActual'), value: prevActual?.hiring_rate || 0 },
          { name: this.translocoService.translate('predictions.chart.currentPredicted'), value: currentPredicted.hiring_rate },
        ],
      },
      {
        name: this.translocoService.translate('predictions.metrics.supplyDemandRatio'),
        series: [
          { name: this.translocoService.translate('predictions.chart.prevPredicted'), value: prevPredicted?.supply_demand_ratio || 0 },
          { name: this.translocoService.translate('predictions.chart.prevActual'), value: prevActual?.supply_demand_ratio || 0 },
          { name: this.translocoService.translate('predictions.chart.currentPredicted'), value: currentPredicted.supply_demand_ratio },
        ],
      },
    ];
  }

  /**
   * Get severity badge class
   */
  getSeverityClass(severity: string): string {
    switch (severity) {
      case 'high':
        return 'severity-high';
      case 'medium':
        return 'severity-medium';
      case 'low':
        return 'severity-low';
      default:
        return '';
    }
  }

  /**
   * Get priority badge class
   */
  getPriorityClass(priority: string): string {
    switch (priority) {
      case 'high':
        return 'priority-high';
      case 'medium':
        return 'priority-medium';
      case 'low':
        return 'priority-low';
      default:
        return '';
    }
  }

  /**
   * Get trend icon based on trend value
   */
  getTrendIcon(trend: string): string {
    switch (trend) {
      case 'growing':
        return 'trending_up';
      case 'cooling':
        return 'trending_down';
      case 'stable':
        return 'trending_flat';
      default:
        return 'help_outline';
    }
  }

  /**
   * Get trend class for styling
   */
  getTrendClass(trend: string): string {
    switch (trend) {
      case 'growing':
        return 'trend-growing';
      case 'cooling':
        return 'trend-cooling';
      case 'stable':
        return 'trend-stable';
      default:
        return '';
    }
  }

  /**
   * Format percentage with sign
   */
  formatPercentage(value: number): string {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    return formatDateLocalized(dateString, this.translocoService.getActiveLang());
  }

  /**
   * Format month for display
   */
  formatMonth(monthString: string): string {
    return formatMonthLocalized(monthString, this.translocoService.getActiveLang());
  }

  /**
   * Get confidence level text
   */
  getConfidenceLevel(score: number): string {
    if (score >= 0.8) return 'predictions.confidence.veryHigh';
    if (score >= 0.6) return 'predictions.confidence.high';
    if (score >= 0.4) return 'predictions.confidence.medium';
    return 'predictions.confidence.low';
  }

  /**
   * Get confidence level class
   */
  getConfidenceClass(score: number): string {
    if (score >= 0.8) return 'confidence-very-high';
    if (score >= 0.6) return 'confidence-high';
    if (score >= 0.4) return 'confidence-medium';
    return 'confidence-low';
  }

  /**
   * Get translated text based on current language
   */
  getTranslatedText(text: TranslatedText): string {
    const currentLang = this.translocoService.getActiveLang();

    switch (currentLang) {
      case 'en':
        return text.en;
      case 'cz':
        return text.cz;
      case 'sk':
        return text.sk;
      default:
        return text.en; // Fallback to English
    }
  }

  /**
   * Check if hot sectors data is available
   */
  hasHotSectors(): boolean {
    return !!(this.predictions?.hotSectors && this.predictions.hotSectors.length > 0);
  }

  /**
   * Check if cooling sectors data is available
   */
  hasCoolingSectors(): boolean {
    return !!(this.predictions?.coolingSectors && this.predictions.coolingSectors.length > 0);
  }

  /**
   * Check if risk factors data is available
   */
  hasRiskFactors(): boolean {
    return !!(this.predictions?.riskFactors && this.predictions.riskFactors.length > 0);
  }

  /**
   * Check if recommendations data is available
   */
  hasRecommendations(): boolean {
    return !!(this.predictions?.recommendations && this.predictions.recommendations.length > 0);
  }

  /**
   * Check if AI reasoning data is available
   */
  hasReasoning(): boolean {
    return !!(
      this.predictions?.reasoning &&
      ((this.predictions.reasoning.main_drivers && this.predictions.reasoning.main_drivers.length > 0) ||
        (this.predictions.reasoning.patterns_applied && this.predictions.reasoning.patterns_applied.length > 0) ||
        (this.predictions.reasoning.seasonal_adjustments && this.predictions.reasoning.seasonal_adjustments.length > 0) ||
        (this.predictions.reasoning.learnings_applied && this.predictions.reasoning.learnings_applied.length > 0))
    );
  }

  /**
   * Check if confidence analysis data is available
   */
  hasConfidenceAnalysis(): boolean {
    return !!(
      this.predictions?.confidenceAnalysis &&
      ((this.predictions.confidenceAnalysis.high_confidence_factors && this.predictions.confidenceAnalysis.high_confidence_factors.length > 0) ||
        (this.predictions.confidenceAnalysis.uncertainty_factors && this.predictions.confidenceAnalysis.uncertainty_factors.length > 0))
    );
  }
}
